import { useQuery } from "@tanstack/react-query";
import type {
  FilterTree,
  HubspotPartnerDealRelationship,
} from "@colab/shared";
import { apiFetch, ApiClientError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { encodeFilterTreeParam, hashFilterTree } from "./filter-node-mapping";

// GET /api/admin/deals?showClosed=<0|1>&filter=<b64-json>
//
// Backs the Kanban surface on /admin/deals. The worker bundles attribution
// aggregation + HubSpot pipeline stage fetch + closed-stage filtering into
// one response (`{ stages, grouped }`) so the page renders without a
// per-stage round-trip.
//
// `showClosed` is part of the query key so the toggle doesn't churn the
// default cache. Sub-bucket 18 extends the hook with `filterNode` so
// admin_deal Saved Views can scope the read; the worker applies the filter
// in-memory after the join (deals volume is ~100, fine for post-fetch).

export type AdminKanbanDeal = {
  hubspot_deal_id: string;
  hubspot_url: string;
  deal_name: string | null;
  deal_amount: number | null;
  deal_currency_code: string | null;
  deal_close_date: string | null;
  deal_stage_id: string | null;
  attributions: Array<{
    relationship: HubspotPartnerDealRelationship;
    partner_id: string;
    partner_name: string;
  }>;
};

export type SalesPipelineStage = {
  id: string;
  label: string;
  displayOrder: number;
  isClosed: boolean;
  probability: number | null;
};

export type AdminKanbanResponse = {
  stages: SalesPipelineStage[];
  grouped: Record<string, AdminKanbanDeal[]>;
};

export function useAdminDeals(
  showClosed: boolean,
  filterNode: FilterTree | null = null,
  opts: { enabled?: boolean } = {},
) {
  const filterParam = encodeFilterTreeParam(filterNode);
  const filterHash = hashFilterTree(filterNode);
  return useQuery<AdminKanbanResponse, ApiClientError>({
    queryKey: [
      ...queryKeys.admin.deals.kanban(showClosed),
      filterHash,
    ] as const,
    enabled: opts.enabled ?? true,
    queryFn: () => {
      const sp = new URLSearchParams();
      if (showClosed) sp.set("showClosed", "1");
      if (filterParam) sp.set("filter", filterParam);
      const qs = sp.toString();
      return apiFetch<AdminKanbanResponse>(
        `/admin/deals${qs ? `?${qs}` : ""}`,
      );
    },
  });
}
